import axios, { CancelTokenSource } from 'axios'
import { Input } from 'components/Input'
import { TOption } from 'components/Select/types'
import { DEFAULT_PAGE } from 'components/Table/constants'
import { Typography, TYPOGRAPHY_WEIGHT } from 'components/Typography'
import {
  CommonsUser,
  CommonsUsersFilters,
  CommonsUsersFiltersFunc,
  CommonsUsersParams,
  CommonsUsersRequestData
} from 'features/MyCommunity/Commons/CommonsUsers/types'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { getAllowedRolesList } from 'redux/store/myCommunity/getters'
import { getUser } from 'redux/store/user/getters'
import { ReactComponent as Close16 } from '../../../../assets/svg/Clos16.svg'
import { ReactComponent as Search16 } from '../../../../assets/svg/Search16.svg'
import { ReactComponent as Plus16 } from '../../../../assets/svg/Plus16.svg'
import { ReactComponent as Edit16 } from '../../../../assets/svg/Edit16.svg'
import { BTN_TXT, PLACEHOLDERS } from 'constants/txt'
import { Button, BUTTON_TYPES } from 'components/Button'
import { UserArchivePopup } from 'pages/CRUDUser/UserArchivePopup'
import { Table } from 'components/Table'
import { Tooltip } from 'components/Tooltip/Tooltip'
import useRouter from 'hooks/useRouter'
import { setLoading } from 'redux/store/common/slice'
import { fullFormatPhoneNumber, isTableHasParams } from 'helper/common'
import { ability, ACTIONS, ROLES, SUBJECTS } from 'features/Permission'
import { useDebounce } from 'hooks/hooks'
import { getCommonsUsers } from '../../Commons/CommonsUsers/api'
import { routes } from '../../../../router'
import { MY_COMMUNITY_KEYS } from '../../constants'
import { ColumnsType } from 'antd/es/table'
import { AVAILABLE_FOR_CCS } from '../../Commons/CommonsUsers/constants'
import { onStartChat } from '../../utils'
import { ALL_ROLES_OPTION, USER_STATUSES } from 'constants/common'
import { Select } from '../../../../components/Select'
import './styles.scss'
import { columns } from './constants'

export const CommonsUsers = () => {
  const allowedRoles = useSelector(getAllowedRolesList)

  const [searchValue, setSearchValue] = useState('')
  const [isArchiveOpen, setIsArchiveOpen] = useState(false)
  const [selectedByActionsUser, setSelectedByActionsUser] =
    useState<CommonsUser | null>(null)
  const [rolesList, setRolesList] = useState<TOption[]>([])
  const [activeTab, setActiveTab] = useState<string>('')
  const [tableData, setTableData] = useState<CommonsUser[]>([])
  const [selectedRowKeys, setSelectedRowKeys] = useState([])
  const [sortParams, setSortParams] = useState<CommonsUsersRequestData>()
  const [firstLoad, setFirstLoad] = useState(true)
  const [totalNumber, setTotalNumber] = useState(0)
  const [filters, setFilters] = useState<CommonsUsersFilters>({})
  const [pageInfo, setPageInfo] = useState({ ...DEFAULT_PAGE })
  const [selectedRows, setSelectedRows] = useState<CommonsUser[]>([])
  const timeoutRef = useRef<ReturnType<typeof setTimeout>>()
  const [tableColumns, setTableColumns] = useState<ColumnsType<CommonsUser>>([])
  const tableDataTokenRef = useRef<CancelTokenSource>()
  const user = useSelector(getUser)

  const dispatch = useDispatch()
  const { push } = useRouter()

  const debouncedSearch = useDebounce(searchValue, 500)

  const isCCS = useMemo(
    () => Boolean(user.role === ROLES.COMMONS_CONTRACT_STEWARD),
    [user.role]
  )

  const isNotAvailableForCCS = useMemo(() => {
    if (!activeTab) return false
    return isCCS && !AVAILABLE_FOR_CCS.find((role) => role === activeTab)
  }, [activeTab, isCCS])

  const canEditUser = ability.can(ACTIONS.CRUD, SUBJECTS.COMMONS_USERS)

  useEffect(() => {
    return () => {
      if (!!tableDataTokenRef.current) {
        tableDataTokenRef.current.cancel()
      }
    }
  }, [])

  useEffect(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
    }
    timeoutRef.current = setTimeout(
      () => getTableData({ ...sortParams, ...filters }),
      firstLoad ? 0 : 500
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch, activeTab])

  const onChangeRole = (e) => {
    setActiveTab(e)
    setSortParams({})
    onCancelSelection()
  }

  const handleTableChange = (pagination, _filters, sorter) => {
    const page = {
      pageNumber: pagination.current,
      pageSize: pagination.pageSize
    }
    getTableData({
      sortField: sorter.field,
      sortOrder: sorter.order,
      filters,
      page
    })
    setPageInfo(page)
  }

  const handleChangePageSize = (pageSize) => {
    const page = {
      pageNumber: 1,
      pageSize
    }
    getTableData({
      ...sortParams,
      filters,
      page
    })
    setPageInfo(page)
  }

  const applyFilters: CommonsUsersFiltersFunc = (field, appliedFilters) => {
    const newFilters = { ...filters, [field]: appliedFilters }
    setFilters(newFilters)
    getTableData({ ...sortParams, filters: newFilters })
  }

  const getTableData = ({
    sortField,
    sortOrder,
    filters,
    page
  }: CommonsUsersRequestData) => {
    dispatch(setLoading(true))
    if (!!tableDataTokenRef.current) {
      tableDataTokenRef.current.cancel()
    }
    const params: CommonsUsersParams = { ...filters }
    if (searchValue) {
      params.search = searchValue
    }

    if (sortField) {
      if (sortOrder) {
        params.ordering = `${sortOrder === 'descend' ? '-' : ''}${sortField}`
      }
      setSortParams({
        sortField,
        sortOrder
      })
    }
    const dataPage = page ? page : pageInfo
    params.limit = dataPage.pageSize
    params.offset = (dataPage.pageNumber - 1) * dataPage.pageSize
    tableDataTokenRef.current = axios.CancelToken.source()
    getCommonsUsers(activeTab, {
      cancelToken: tableDataTokenRef.current?.token,
      params
    })
      .then((resp) => {
        if (!resp.data?.results) {
          return
        }
        setTableColumns(
          columns({
            applyFilters,
            filters,
            isNotAvailableForCCS,
            onStartChat,
            role: activeTab ?? '',
            onArchive,
            canEditUser: canEditUser && !isNotAvailableForCCS
          })
        )
        setTableData(
          resp.data.results.map((i) => ({
            ...i,
            name: i.first_name,
            phone_number: fullFormatPhoneNumber(i.phone_number),
            key: i.uuid
          }))
        )
        setTotalNumber(resp.data.count)
        if (firstLoad) {
          setFirstLoad(false)
        }
      })
      .finally(() => dispatch(setLoading(false)))
  }

  const handleSelectRow = (selectedRowKeys, selectedRows) => {
    setSelectedRowKeys(selectedRowKeys)
    setSelectedRows(selectedRows)
  }

  useEffect(() => {
    if (!!allowedRoles.length) {
      const roles =
        allowedRoles.find((i) => i.tab_name === 'commons')?.roles || []
      setRolesList([
        ALL_ROLES_OPTION,
        ...roles.map((role) => ({
          value: role.value,
          label: role.label
        }))
      ])
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allowedRoles.length])

  const createUser = () =>
    push(`${routes.createUserProfile}/new?fromCommunity=true`, {
      state: { redirectTab: MY_COMMUNITY_KEYS.my_organization }
    })

  const onArchive = useCallback((user) => {
    setIsArchiveOpen(true)
    setSelectedByActionsUser(user)
  }, [])

  const onEditUser = useCallback(() => {
    push(
      `${routes.createUserProfile}/${selectedRowKeys[0]}?fromCommunity=true`,
      {
        state: { redirectTab: MY_COMMUNITY_KEYS.my_organization }
      }
    )
  }, [push, selectedRowKeys])

  const onCancelSelection = useCallback(() => {
    setIsArchiveOpen(false)
    setSelectedByActionsUser(null)
    setSelectedRowKeys([])
    setSelectedRows([])
  }, [])

  return (
    <div className="column gap-16 full-width">
      <div className="column gap-4">
        <div className="row space-between">
          <div className="row gap-8 align-center">
            <Select
              options={rolesList}
              propsSelect={{
                onChange: onChangeRole,
                value: activeTab
              }}
              className="user-role-filter"
            />
            {!!selectedRowKeys.length ? (
              <Typography.Body1
                className="table-wrapper__select-title"
                weight={TYPOGRAPHY_WEIGHT.SEMI_BOLD}
              >
                Selected {selectedRowKeys.length} of {totalNumber}
              </Typography.Body1>
            ) : (
              <Input
                className="vendor-users-list__search allow-clear"
                propsInput={{
                  allowClear: { clearIcon: <Close16 /> },
                  placeholder: PLACEHOLDERS.SEARCH,
                  prefix: <Search16 />,
                  value: searchValue,
                  onChange: (e) => setSearchValue(e.target.value)
                }}
              />
            )}
          </div>
          <div className="row gap-8">
            {canEditUser && !selectedRowKeys.length && (
              <Button
                type={BUTTON_TYPES.PRIMARY}
                icon={<Plus16 />}
                onClick={createUser}
                upperCase
              >
                {BTN_TXT.CREATE_USER}
              </Button>
            )}
            {selectedRowKeys.length === 1 && (
              <Button
                type={BUTTON_TYPES.PRIMARY}
                icon={<Edit16 />}
                upperCase
                onClick={onEditUser}
              >
                {BTN_TXT.EDIT}
              </Button>
            )}
            {((!!selectedRows.length &&
              selectedRows.filter((i) => i.status !== USER_STATUSES.ARCHIVED)
                .length === selectedRows.length) ||
              isArchiveOpen) && (
              <UserArchivePopup<CommonsUser>
                selected={
                  isArchiveOpen && selectedByActionsUser
                    ? [selectedByActionsUser]
                    : selectedRows
                }
                callback={() => {
                  onCancelSelection()
                  getTableData({ ...sortParams, ...filters })
                }}
                isTable
                isOpenFromTheOutside={isArchiveOpen}
                setIsOpenFromTheOutside={setIsArchiveOpen}
              />
            )}
          </div>
        </div>
        <Table<CommonsUser>
          dataSource={!tableColumns.length ? [] : tableData}
          columns={tableColumns}
          emptyText="No users found"
          className="community-users-table"
          onChange={handleTableChange}
          onChangePage={handleChangePageSize}
          pageSize={pageInfo.pageSize}
          pagination={{
            pageSize: pageInfo.pageSize,
            current: pageInfo.pageNumber,
            total: totalNumber
          }}
          hasSearchOrFilters={isTableHasParams<CommonsUsersFilters>(
            debouncedSearch,
            filters
          )}
          createButton={
            canEditUser
              ? {
                  buttonText: BTN_TXT.CREATE_USER,
                  availability: true,
                  action: createUser
                }
              : undefined
          }
          rowSelection={
            canEditUser && !isNotAvailableForCCS
              ? {
                  selectedRowKeys,
                  onChange: handleSelectRow,
                  getCheckboxProps: (record) => ({
                    disabled:
                      record.uuid === user.uuid ||
                      ('can_be_managed' in record && !record?.can_be_managed)
                  }),
                  renderCell: (_checked, record, _, node) => {
                    if ('can_be_managed' in record && !record?.can_be_managed) {
                      const hasSameRole = record?.role === user.role
                      return (
                        <Tooltip
                          title={
                            hasSameRole
                              ? 'You do not have permission to manage users with the same role'
                              : 'You do not have permission to manage users of this role'
                          }
                          overlayInnerStyle={{
                            display: 'block',
                            width: 264
                          }}
                          placement="topLeft"
                        >
                          {node}
                        </Tooltip>
                      )
                    }
                    return node
                  }
                }
              : undefined
          }
        />
      </div>
    </div>
  )
}
